import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../shell/Can.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import {
  getUser,
  activateUser,
  deactivateUser,
  unlockUser,
  adminSetPassword,
  USER_STATUS_LABELS,
  type User,
} from '../../api/usersApi.js';

interface Props {
  readonly userId: string;
  /** Lets the host screen refresh its own copy after a status change. */
  readonly onChanged?: (user: User) => void;
}

/**
 * Account actions on an existing user: activate, deactivate, unlock and the
 * administrator-set password.
 *
 * Extracted so the live edit screen and the older edit page share one
 * implementation — these were reachable from neither until the panel existed
 * as a component that could be mounted on both.
 */
export function UserAccountActionsPanel({ userId, onChanged }: Props): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The administrator agrees a password with the user rather than reading out a
  // generated one, so it is typed twice and never echoed back by the server.
  const [pwOpen, setPwOpen] = useState(false);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');

  useEffect(() => {
    let alive = true;
    getUser(userId, token)
      .then((u) => { if (alive) setStatus(u.userStatus); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [userId, token]);

  const runAction = useCallback(async (fn: () => Promise<User>, label: string) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const next = await fn();
      setStatus(next.userStatus);
      onChanged?.(next);
      setNotice(label);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [onChanged]);

  async function onSavePassword() {
    setError(null);
    setNotice(null);
    if (pw1 !== pw2) {
      setError(t('users.edit.err.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await adminSetPassword(userId, pw1, token);
      setPwOpen(false);
      setPw1('');
      setPw2('');
      setNotice(t('users.edit.passwordChanged'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="user-status">
      <p>{t('users.edit.status')}: <strong>{USER_STATUS_LABELS[status] ?? status}</strong></p>
      <Can path="/admin/users" action="edit">
        {status === 'ACTIVE' ? (
          <button type="button" onClick={() => void runAction(() => deactivateUser(userId, token), t('users.edit.deactivated'))} disabled={busy}>{t('users.edit.deactivate')}</button>
        ) : null}
        {status !== 'ACTIVE' && status !== 'ARCHIVED' && status !== 'LOCKED' ? (
          <button type="button" onClick={() => void runAction(() => activateUser(userId, token), t('users.edit.activated'))} disabled={busy}>{t('users.edit.activate')}</button>
        ) : null}
        {status === 'LOCKED' ? (
          <button type="button" onClick={() => void runAction(() => unlockUser(userId, token), t('users.edit.unlocked'))} disabled={busy}>{t('users.edit.unlock')}</button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          data-testid={`reset-password-${userId}`}
          onClick={() => setPwOpen(true)}
          disabled={busy}
        >
          {t('users.edit.resetPassword')}
        </Button>
      </Can>
      {pwOpen ? (
        <div aria-label="set-password">
          <label htmlFor="e-pw1">{t('users.edit.newPassword')}</label>
          <input id="e-pw1" aria-label="new-password" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} />
          <label htmlFor="e-pw2">{t('users.edit.confirmPassword')}</label>
          <input id="e-pw2" aria-label="confirm-password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <Button type="button" variant="primary" data-testid="save-password" onClick={() => void onSavePassword()} disabled={busy}>
            {t('users.edit.savePassword')}
          </Button>
        </div>
      ) : null}
      {notice ? <p role="status" data-testid="status-notice">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </section>
  );
}
